import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Delete,
  Store as StoreIcon,
  LocationOn,
  Search,
  Visibility,
  Straighten
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { GoogleMap, useJsApiLoader, Polygon, Marker, StandaloneSearchBox, InfoWindow } from '@react-google-maps/api';
import { podAPI } from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';
import api from '../../utils/api';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBC28IfOmHnlcxvQo1sW9sduMvJ9jENTlE';


const mapContainerStyle = {
  width: '100%',
  height: '500px'
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.2090
};

const toRadians = (deg) => (deg * Math.PI) / 180;

const calculateHaversineDistanceMeters = (pointA, pointB) => {
  if (!pointA || !pointB) return 0;
  const R = 6371000; // meters
  const lat1 = toRadians(pointA.lat);
  const lat2 = toRadians(pointB.lat);
  const dLat = toRadians(pointB.lat - pointA.lat);
  const dLng = toRadians(pointB.lng - pointA.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get travel distance between two points using Google Maps Distance Matrix API
 * @param {Object} pointA - {lat, lng}
 * @param {Object} pointB - {lat, lng}
 * @returns {Promise<number>} Distance in meters
 */
const getTravelDistanceMeters = async (pointA, pointB) => {
  try {
    const response = await podAPI.calculateTravelDistance({
      origin: { lat: pointA.lat, lng: pointA.lng },
      destination: { lat: pointB.lat, lng: pointB.lng }
    });
    
    if (response.data.success && response.data.distance) {
      return response.data.distance; // Distance in meters
    } else {
      throw new Error(response.data.message || 'No route found');
    }
  } catch (error) {
    console.error('Google Maps Distance Matrix API error:', error);
    throw error;
  }
};

/**
 * Convert HSL to Hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color code
 */
const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Generate a consistent color for a pod based on its ID
 * @param {string} podId - Pod ID
 * @returns {string} Hex color code
 */
const getPodColor = (podId) => {
  if (!podId) return '#FF0000'; // Default red
  
  // Hash the pod ID to generate a consistent color
  let hash = 0;
  for (let i = 0; i < podId.length; i++) {
    hash = podId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a color with good saturation and brightness
  const hue = Math.abs(hash) % 360;
  // Use HSL to create vibrant colors, then convert to hex
  return hslToHex(hue, 70, 50);
};

const PodsManagement = () => {
  const { actions } = useApp();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [pods, setPods] = useState([]);
  const [allPods, setAllPods] = useState([]); // All pods from all stores
  const [viewAllPods, setViewAllPods] = useState(false); // Toggle to view all pods
  const [loading, setLoading] = useState(false);
  const [podsLoading, setPodsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [podToDelete, setPodToDelete] = useState(null);
  const [creatingDefaultPods, setCreatingDefaultPods] = useState(false);
  const [creatingDefaultPodForStore, setCreatingDefaultPodForStore] = useState(false);
  
  // Map and drawing state
  const [map, setMap] = useState(null);
  const [dialogMap, setDialogMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const searchBoxRef = useRef(null);
  const measurePolylineRef = useRef(null);

  // Measure distance state (main map)
  const [isMeasuringDistance, setIsMeasuringDistance] = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]); // [{lat,lng},{lat,lng}]
  const [measuredDistanceMeters, setMeasuredDistanceMeters] = useState(0);
  const [isCalculatingTravelDistance, setIsCalculatingTravelDistance] = useState(false);
  
  // Form state
  const [podName, setPodName] = useState('');
  const [podActive, setPodActive] = useState(true);
  
  // New pod creation state
  const [selectedPodCenter, setSelectedPodCenter] = useState(null);
  const [travelDistanceMeters, setTravelDistanceMeters] = useState(0);
  const [podGenerated, setPodGenerated] = useState(false);
  const [showDistanceInput, setShowDistanceInput] = useState(false);
  const [previewPolygon, setPreviewPolygon] = useState(null);
  const [generatingPolygon, setGeneratingPolygon] = useState(false);
  
  // Selected pod for info window (main map)
  const [selectedPod, setSelectedPod] = useState(null);
  const [selectedPodPosition, setSelectedPodPosition] = useState(null);
  
  // Selected pod for info window (dialog map)
  const [selectedDialogPod, setSelectedDialogPod] = useState(null);
  const [selectedDialogPodPosition, setSelectedDialogPodPosition] = useState(null);
  
  // Visualize Orders state
  const [visualizeOrdersDialogOpen, setVisualizeOrdersDialogOpen] = useState(false);
  const [orderFromDate, setOrderFromDate] = useState('');
  const [orderToDate, setOrderToDate] = useState('');
  const [ordersForVisualizationRaw, setOrdersForVisualizationRaw] = useState([]); // last fetched orders (with coords), unfiltered by store
  const [ordersForVisualization, setOrdersForVisualization] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrderMarker, setSelectedOrderMarker] = useState(null);
  
  // Locate marker state
  const [locateInput, setLocateInput] = useState('');
  const [locatedMarker, setLocatedMarker] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['drawing', 'places']
  });

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (selectedStoreId) {
      fetchPods(selectedStoreId);
      const store = stores.find(s => s.id === selectedStoreId);
      if (store && store.location) {
        const [lat, lng] = store.location.split(',').map(coord => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter({ lat, lng });
          // Pan map to store location if map is already loaded
          if (map) {
            map.panTo({ lat, lng });
            map.setZoom(13);
          }
        }
      }
    } else {
      setPods([]);
    }
  }, [selectedStoreId, stores, map]);

  // Fit map bounds to show all pods when viewing all pods
  useEffect(() => {
    if (viewAllPods && map && allPods.length > 0 && !podsLoading && window.google?.maps) {
      // Use a small delay to ensure map and pods are fully rendered
      const timeoutId = setTimeout(() => {
        const bounds = new window.google.maps.LatLngBounds();
        let hasBounds = false;

        // Add all pod coordinates to bounds
        allPods.filter(p => p.active && p.type === 'polygon').forEach((pod) => {
          const path = getPolygonPath(pod);
          if (path.length > 0) {
            path.forEach(point => {
              bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
              hasBounds = true;
            });
          }
        });

        // Also add all store locations to bounds
        stores.filter(s => s.location).forEach((store) => {
          const [lat, lng] = store.location.split(',').map(coord => parseFloat(coord.trim()));
          if (!isNaN(lat) && !isNaN(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
            hasBounds = true;
          }
        });

        // Fit bounds if we have any valid coordinates
        if (hasBounds) {
          try {
            map.fitBounds(bounds, { padding: 50 }); // Add padding for better view
            // Set a minimum zoom level to prevent zooming in too much
            const listener = window.google.maps.event.addListener(map, 'bounds_changed', () => {
              if (map.getZoom() > 15) {
                map.setZoom(15);
              }
              window.google.maps.event.removeListener(listener);
            });
          } catch (error) {
            console.error('Error fitting bounds:', error);
          }
        }
      }, 300); // Increased delay to ensure all data is ready

      return () => clearTimeout(timeoutId);
    }
  }, [viewAllPods, map, allPods, stores, podsLoading]);

  // When store selection changes, re-fetch orders with the new store filter
  // Note: We don't re-filter client-side anymore since API handles filtering
  // This effect is kept for clearing selected marker when store changes
  useEffect(() => {
    if (ordersForVisualizationRaw && ordersForVisualizationRaw.length > 0) {
      // If orders were already fetched, clear the selected marker when store changes
      setSelectedOrderMarker(null);
    }
  }, [selectedStoreId]);

  // Update map center when dialog opens and pan to store location
  useEffect(() => {
    if (createDialogOpen && selectedStore && selectedStore.location) {
      const [lat, lng] = selectedStore.location.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
        // Pan dialog map to store location if map is already loaded
        if (dialogMap) {
          dialogMap.panTo({ lat, lng });
          dialogMap.setZoom(13);
        }
      }
    }
  }, [createDialogOpen, selectedStore, dialogMap]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const storesData = await cacheEndpoints.stores.getAll();
      setStores(storesData);
    } catch (error) {
      actions.showSnackbar('Error fetching stores', 'error');
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPods = async (storeId) => {
    try {
      setPodsLoading(true);
      // Clear store-specific pods and selected pod when fetching store-specific pods
      setPods([]);
      setSelectedPod(null);
      setSelectedPodPosition(null);
      
      const response = await podAPI.getPodsByStore(storeId);
      const fetchedPods = response.data.pods || [];
      setPods(fetchedPods);
    } catch (error) {
      actions.showSnackbar('Error fetching pods', 'error');
      console.error('Error fetching pods:', error);
    } finally {
      setPodsLoading(false);
    }
  };

  const fetchAllPods = async () => {
    try {
      setPodsLoading(true);
      // Clear store-specific pods and selected pod when fetching all pods
      setPods([]);
      setSelectedPod(null);
      setSelectedPodPosition(null);
      
      const response = await podAPI.getAllPods(true);
      const fetchedPods = response.data.pods || [];
      setAllPods(fetchedPods);
      
      // Trigger map bounds fit after pods are loaded
      // This will be handled by the useEffect that watches allPods
    } catch (error) {
      actions.showSnackbar('Error fetching all pods', 'error');
      console.error('Error fetching all pods:', error);
    } finally {
      setPodsLoading(false);
    }
  };

  const handleCreateDefaultPods = async () => {
    if (!window.confirm('This will DELETE ALL existing pods and create new default pods for all active stores using each store\'s delivery radius. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      setCreatingDefaultPods(true);
      const response = await podAPI.createDefaultPodsForAllStores();
      const { results } = response.data;
      
      const deletedCount = results.deleted || 0;
      const createdCount = results.created.length;
      const skippedCount = results.skipped.length;
      const errorCount = results.errors.length;
      
      let message = `Deleted ${deletedCount} existing pods. Created ${createdCount} default pods`;
      if (skippedCount > 0) {
        message += `, ${skippedCount} skipped`;
      }
      if (errorCount > 0) {
        message += `, ${errorCount} errors`;
      }
      
      actions.showSnackbar(message, createdCount > 0 ? 'success' : 'info');
      
      // Refresh pods if a store is selected
      if (selectedStoreId) {
        fetchPods(selectedStoreId);
      }
      if (viewAllPods) {
        fetchAllPods();
      }
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error creating default pods',
        'error'
      );
    } finally {
      setCreatingDefaultPods(false);
    }
  };

  const handleCreateDefaultPodForStore = async () => {
    if (!selectedStoreId || !selectedStore) {
      actions.showSnackbar('Please select a store first', 'error');
      return;
    }

    const confirmMessage = `This will DELETE all existing pods for "${selectedStore.storeName}" and create a new default pod using its delivery radius. Continue?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setCreatingDefaultPodForStore(true);
      const response = await podAPI.createDefaultPodForStore(selectedStoreId);
      const { deleted, created } = response.data;

      const message = `Deleted ${deleted} pods for ${selectedStore.storeName}. Created default pod.`;
      actions.showSnackbar(message, 'success');

      // Refresh pods for this store
      fetchPods(selectedStoreId);
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error creating default pod for store',
        'error'
      );
    } finally {
      setCreatingDefaultPodForStore(false);
    }
  };

  const handleToggleViewAllPods = () => {
    const newViewAll = !viewAllPods;
    
    // Clear ALL state first to prevent showing stale data or duplicate rendering
    setSelectedPod(null);
    setSelectedPodPosition(null);
    setPods([]);
    setAllPods([]);

    // If switching to "View All Pods", clear selected store so:
    // - store dropdown is no longer the active filter
    // - visualized orders automatically show for ALL stores (via selectedStoreId effect)
    if (newViewAll) {
      setSelectedStoreId('');
      setSelectedStore(null);
    }
    
    // Set the new view state after clearing
    setViewAllPods(newViewAll);
    
    // Fetch the appropriate data (fetch functions will also clear selectedPod)
    if (newViewAll) {
      fetchAllPods();
    } else if (selectedStoreId) {
      fetchPods(selectedStoreId);
    }
  };

  const handleFetchOrdersForVisualization = async () => {
    if (!orderFromDate || !orderToDate) {
      actions.showSnackbar('Please select both start and end dates', 'error');
      return;
    }

    const fromDate = new Date(orderFromDate);
    const toDate = new Date(orderToDate);
    
    // Validate date range
    if (toDate < fromDate) {
      actions.showSnackbar('End date must be after start date', 'error');
      return;
    }

    // Check if date range is more than 31 days
    const daysDiff = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 31) {
      actions.showSnackbar('Date range cannot exceed 31 days', 'error');
      return;
    }

    try {
      setLoadingOrders(true);
      const params = {
        from_date: orderFromDate,
        to_date: orderToDate,
        limit: 1000, // Fetch more orders to visualize
        ...(selectedStoreId && { storeId: selectedStoreId }), // Add storeId filter if store is selected
        ...(selectedStore?.branchId && { branch_id: selectedStore.branchId }) // Add branch_id filter if store is selected
      };

      const response = await api.get('/orders', { params });
      if (response.data.success) {
        const orders = response.data.orders || [];
        // Filter orders that have valid delivery coordinates
        const ordersWithCoords = orders.filter(order => {
          const latLng = order.deliveryAddress?.latLng || order.deliveryAddress?.latLong;
          return latLng && latLng.lat && latLng.lng && 
                 typeof latLng.lat === 'number' && typeof latLng.lng === 'number';
        });

        // Store raw list so switching stores can re-filter without refetch
        setOrdersForVisualizationRaw(ordersWithCoords);

        // Orders are already filtered by storeId in the API, so use all orders with coords
        setOrdersForVisualization(ordersWithCoords);
        setVisualizeOrdersDialogOpen(false);
        actions.showSnackbar(`Showing ${ordersWithCoords.length} orders on map`, 'success');
        
        // If no store is selected, switch to view all pods so the map has all delivery pods context.
        // If a store is selected, keep current store pods view.
        if (!selectedStoreId && !viewAllPods) {
          setViewAllPods(true);
          fetchAllPods();
        }
      } else {
        actions.showSnackbar('Failed to fetch orders', 'error');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      actions.showSnackbar(error.response?.data?.message || 'Error fetching orders', 'error');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleCloseVisualizeOrdersDialog = () => {
    setVisualizeOrdersDialogOpen(false);
    setOrderFromDate('');
    setOrderToDate('');
  };

  const handleClearOrders = () => {
    setOrdersForVisualizationRaw([]);
    setOrdersForVisualization([]);
    setSelectedOrderMarker(null);
    actions.showSnackbar('Orders cleared from map', 'info');
  };

  const handleStoreChange = (event) => {
    const storeId = event.target.value;
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.id === storeId);
    setSelectedStore(store || null);
    setViewAllPods(false); // Reset view all when selecting a store
    resetDrawing();
    // Clear pods immediately when switching stores
    setPods([]);
    setSelectedPod(null);
    setSelectedPodPosition(null);
    if (storeId) {
      fetchPods(storeId);
    }
  };

  const resetDrawing = () => {
    setPodName('');
    setPodActive(true);
    setSelectedPodCenter(null);
    setTravelDistanceMeters(0);
    setPodGenerated(false);
    setShowDistanceInput(false);
    setPreviewPolygon(null);
    setGeneratingPolygon(false);
    // Clear any selected pods
    setSelectedPod(null);
    setSelectedDialogPod(null);
    setSelectedPodPosition(null);
    setSelectedDialogPodPosition(null);
  };

  const clearDistanceMeasurement = () => {
    setIsCalculatingTravelDistance(false);
    // Always remove the native polyline overlay, then clear React state.
    try {
      if (measurePolylineRef.current) {
        measurePolylineRef.current.setMap(null);
      }
    } catch (e) {
      // no-op
    } finally {
      measurePolylineRef.current = null;
    }
    setMeasurePoints([]);
    setMeasuredDistanceMeters(0);
  };

  // Draw the measurement line using native google.maps.Polyline to avoid wrapper cleanup edge-cases.
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    // If not actively showing a measurement line, remove any existing overlay.
    if (!isMeasuringDistance || measurePoints.length !== 2) {
      if (measurePolylineRef.current) {
        try {
          measurePolylineRef.current.setMap(null);
        } catch (e) {
          // no-op
        } finally {
          measurePolylineRef.current = null;
        }
      }
      return;
    }

    // Replace any existing line with the latest one.
    if (measurePolylineRef.current) {
      try {
        measurePolylineRef.current.setMap(null);
      } catch (e) {
        // no-op
      } finally {
        measurePolylineRef.current = null;
      }
    }

    const polyline = new window.google.maps.Polyline({
      path: measurePoints,
      geodesic: true,
      strokeColor: '#7B1FA2',
      strokeOpacity: 0.9,
      strokeWeight: 4,
    });
    polyline.setMap(map);
    measurePolylineRef.current = polyline;

    return () => {
      try {
        polyline.setMap(null);
      } catch (e) {
        // no-op
      }
      if (measurePolylineRef.current === polyline) {
        measurePolylineRef.current = null;
      }
    };
  }, [map, isMeasuringDistance, measurePoints]);

  const handleToggleMeasureDistance = () => {
    setIsMeasuringDistance((prev) => {
      const next = !prev;
      if (!next) {
        clearDistanceMeasurement();
      }
      return next;
    });
  };

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    // Pan to store location if available
    if (selectedStore && selectedStore.location) {
      const [lat, lng] = selectedStore.location.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        mapInstance.panTo({ lat, lng });
        mapInstance.setZoom(13);
      }
    }
  }, [selectedStore]);

  const onDialogMapLoad = useCallback((mapInstance) => {
    setDialogMap(mapInstance);
    // Pan to store location if available
    if (selectedStore && selectedStore.location) {
      const [lat, lng] = selectedStore.location.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        mapInstance.panTo({ lat, lng });
        mapInstance.setZoom(13);
      }
    }
  }, [selectedStore]);

  const onSearchBoxLoad = useCallback((ref) => {
    searchBoxRef.current = ref;
  }, []);

  const onPlacesChanged = useCallback(() => {
    const places = searchBoxRef.current?.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      
      // Update map center
      if (dialogMap) {
        dialogMap.panTo({ lat, lng });
        dialogMap.setZoom(15);
      }
      
      setMapCenter({ lat, lng });
      
      // Set pod center when place is searched
      setSelectedPodCenter({ lat, lng });
      setShowDistanceInput(true);
      setPodGenerated(false);
      setPreviewPolygon(null);
    }
  }, [dialogMap]);

  const addMeasurePoint = useCallback(async (lat, lng) => {
    setMeasurePoints((prev) => {
      if (prev.length === 0) {
        setMeasuredDistanceMeters(0);
        setIsCalculatingTravelDistance(false);
        return [{ lat, lng }];
      }
      if (prev.length === 1) {
        const next = [prev[0], { lat, lng }];
        // Calculate travel distance asynchronously
        setIsCalculatingTravelDistance(true);
        getTravelDistanceMeters(next[0], next[1])
          .then((distance) => {
            setMeasuredDistanceMeters(distance);
            setIsCalculatingTravelDistance(false);
          })
          .catch((error) => {
            console.error('Error calculating travel distance:', error);
            setMeasuredDistanceMeters(0);
            setIsCalculatingTravelDistance(false);
          });
        return next;
      }
      // If already have 2 points, start a new measurement from this click
      setMeasuredDistanceMeters(0);
      setIsCalculatingTravelDistance(false);
      return [{ lat, lng }];
    });
  }, []);

  const handleMainMapClick = (event) => {
    // Close any open info windows
    setSelectedPod(null);

    if (!isMeasuringDistance) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    addMeasurePoint(lat, lng);
  };

  const handleDialogMapClick = (event) => {
    // Close any open info windows
    setSelectedDialogPod(null);

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    // Place marker at clicked location
    setSelectedPodCenter({ lat, lng });
    setShowDistanceInput(true);
    setPodGenerated(false);
    setPreviewPolygon(null);
  };

  const handleGeneratePod = async () => {
    if (!selectedPodCenter || travelDistanceMeters <= 0) {
      actions.showSnackbar('Please enter travel distance', 'error');
      return;
    }

    try {
      setGeneratingPolygon(true);
      const response = await podAPI.generatePolygonFromTravelDistance({
        center: selectedPodCenter,
        travelDistanceMeters: travelDistanceMeters
      });
      
      if (response.data.success) {
        const polygon = response.data.polygon;
        // Convert to format expected by Google Maps Polygon component
        const polygonPath = polygon.map(coord => ({
          lat: coord.lat,
          lng: coord.lng
        }));
        setPreviewPolygon(polygonPath);
        setPodGenerated(true);
        actions.showSnackbar('Pod generated successfully', 'success');
      }
    } catch (error) {
      console.error('Error generating polygon:', error);
      actions.showSnackbar(
        error.response?.data?.message || 'Error generating polygon',
        'error'
      );
    } finally {
      setGeneratingPolygon(false);
    }
  };

  const handlePodClick = (pod, event, isDialog = false) => {
    // If measuring distance on main map, allow selecting points even when clicking on pods.
    // Pods are drawn as Polygon/Circle overlays and will otherwise "eat" the click.
    if (!isDialog && isMeasuringDistance && event?.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      addMeasurePoint(lat, lng);
      // Prevent map click and prevent opening pod info while measuring
      event?.stop?.();
      return;
    }

    // Prevent map click when clicking on pod
    event?.stop?.();
    
    // Only handle polygon pods
    if (pod.type !== 'polygon') return;
    
    const path = getPolygonPath(pod);
    const centroid = getPolygonCentroid(path);
    const position = centroid;
    
    if (position) {
      if (isDialog) {
        setSelectedDialogPod(pod);
        setSelectedDialogPodPosition(position);
      } else {
        setSelectedPod(pod);
        setSelectedPodPosition(position);
      }
    }
  };


  const handleCreatePod = async () => {
    if (!podName.trim()) {
      actions.showSnackbar('Pod name is required', 'error');
      return;
    }

    if (!previewPolygon || previewPolygon.length < 3) {
      actions.showSnackbar('Please generate pod first', 'error');
      return;
    }

    try {
      // Convert polygon to Firestore format (object with index keys)
      const coordinatesArray = previewPolygon.map(p => [p.lat, p.lng]);
      
      // Ensure polygon is closed
      const firstCoord = coordinatesArray[0];
      const lastCoord = coordinatesArray[coordinatesArray.length - 1];
      if (Math.abs(firstCoord[0] - lastCoord[0]) > 0.000001 || 
          Math.abs(firstCoord[1] - lastCoord[1]) > 0.000001) {
        coordinatesArray.push([firstCoord[0], firstCoord[1]]);
      }
      
      const coordinatesObj = {};
      coordinatesArray.forEach((coord, index) => {
        coordinatesObj[index.toString()] = coord;
      });

      await podAPI.createPod({
        storeId: selectedStoreId,
        name: podName,
        type: 'polygon', // Always polygon
        shape: {
          coordinates: coordinatesObj
        },
        travelDistance: travelDistanceMeters, // Store travel distance
        active: true
      });
      
      actions.showSnackbar('Pod created successfully', 'success');
      resetDrawing();
      setCreateDialogOpen(false);
      if (viewAllPods) {
        fetchAllPods();
      } else if (selectedStoreId) {
        fetchPods(selectedStoreId);
      }
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error creating pod',
        'error'
      );
    }
  };

  const handleDeleteClick = (pod) => {
    setPodToDelete(pod);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await podAPI.deletePod(podToDelete.id);
      actions.showSnackbar('Pod deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setPodToDelete(null);
      if (viewAllPods) {
        fetchAllPods();
      } else if (selectedStoreId) {
        fetchPods(selectedStoreId);
      }
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error deleting pod',
        'error'
      );
    }
  };

  const handleTogglePodActive = async (pod, newActiveStatus) => {
    try {
      await podAPI.updatePod(pod.id, { active: newActiveStatus });
      actions.showSnackbar(
        `Pod ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
      // Refresh pods list
      if (viewAllPods) {
        fetchAllPods();
      } else if (selectedStoreId) {
        fetchPods(selectedStoreId);
      }
    } catch (error) {
      actions.showSnackbar(
        error.response?.data?.message || 'Error updating pod status',
        'error'
      );
    }
  };

  const getPolygonPath = (pod) => {
    if (pod.type === 'polygon' && pod.shape?.coordinates) {
      // Convert from object format (Firestore) to array format
      let coordinatesArray;
      if (Array.isArray(pod.shape.coordinates)) {
        coordinatesArray = pod.shape.coordinates;
      } else {
        // Convert object with index keys to array
        const keys = Object.keys(pod.shape.coordinates).sort((a, b) => parseInt(a) - parseInt(b));
        coordinatesArray = keys.map(key => pod.shape.coordinates[key]);
      }
      
      return coordinatesArray.map(coord => ({
        lat: coord[0],
        lng: coord[1]
      }));
    }
    return [];
  };


  // Calculate centroid of polygon for label placement
  const getPolygonCentroid = (path) => {
    if (!path || path.length === 0) return null;
    
    let latSum = 0;
    let lngSum = 0;
    
    path.forEach(point => {
      latSum += point.lat;
      lngSum += point.lng;
    });
    
    return {
      lat: latSum / path.length,
      lng: lngSum / path.length
    };
  };


  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading Google Maps</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StoreIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Delivery Pods Management
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={viewAllPods}
                  onChange={handleToggleViewAllPods}
                  disabled={loading}
                  color="primary"
                />
              }
              label={viewAllPods ? 'View Store Pods' : 'View All Pods'}
              labelPlacement="end"
              sx={{ ml: 2 }}
            />
          </Box>
        </Box>

        {/* Store Selection */}
        <Grid container spacing={3}  sx={{ display: 'flex', alignItems: 'center'}}>
          <Grid item xs={12} md={3} >
            <FormControl fullWidth>
              <Select
                sx={{width:'200px'}}
                value={selectedStoreId || ''}
                onChange={handleStoreChange}
                label="Select Store"
                disabled={loading || viewAllPods}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>Select a store</em>
                </MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.storeName} 
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            {selectedStoreId && !viewAllPods && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  resetDrawing(); // Clear any previous drawing state
                  setCreateDialogOpen(true);
                }}
                fullWidth
              >
                Create New Pod
              </Button>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => setVisualizeOrdersDialogOpen(true)}
              fullWidth
            >
              Visualize Orders
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleClearOrders}
              fullWidth
              disabled={ordersForVisualization.length === 0}
            >
              Clear Orders
            </Button>
          </Grid>
        </Grid>

        {(selectedStoreId || viewAllPods) && (
          <>
            {/* Map Display */}
            {isLoaded && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    {viewAllPods ? 'All Pods Visualization (All Stores)' : 'Pod Visualization'}
                    {ordersForVisualization.length > 0 && (
                      <Chip 
                        label={`${ordersForVisualization.length} Orders`} 
                        color="success" 
                        size="small" 
                        sx={{ ml: 2 }} 
                      />
                    )}
                  </Typography>
                </Box>
                <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={viewAllPods ? defaultCenter : mapCenter}
                    zoom={viewAllPods ? 11 : (selectedStore ? 13 : 11)}
                    onLoad={onMapLoad}
                    onClick={handleMainMapClick}
                    options={{
                      streetViewControl: false,
                      fullscreenControl: true,
                      mapTypeControl: true,
                      zoomControl: true,
                    }}
                  >
                    {/* Store markers - show selected store or all stores when viewing all pods */}
                    {viewAllPods ? (
                      // Show all store markers when viewing all pods
                      stores.filter(s => s.location).map((store) => {
                        const [lat, lng] = store.location.split(',').map(coord => parseFloat(coord.trim()));
                        if (!isNaN(lat) && !isNaN(lng)) {
                          return (
                            <Marker
                              key={store.id}
                              position={{ lat, lng }}
                              title={store.storeName}
                              icon={window.google?.maps ? {
                                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                                scaledSize: new window.google.maps.Size(24, 24),
                              } : undefined}
                              label={window.google?.maps ? {
                                text: store.storeName,
                                color: '#000',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              } : undefined}
                            />
                          );
                        }
                        return null;
                      })
                    ) : (
                      // Show selected store marker
                      selectedStore && selectedStore.location && (() => {
                        const [lat, lng] = selectedStore.location.split(',').map(coord => parseFloat(coord.trim()));
                        if (!isNaN(lat) && !isNaN(lng)) {
                          return (
                            <Marker
                              position={{ lat, lng }}
                              title={selectedStore.storeName}
                              icon={window.google?.maps ? {
                                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                                scaledSize: new window.google.maps.Size(32, 32),
                              } : undefined}
                              label={window.google?.maps ? {
                                text: selectedStore.storeName,
                                color: '#000',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              } : undefined}
                            />
                          );
                        }
                        return null;
                      })()
                    )}

                    {/* Display existing pods */}
                    {(viewAllPods ? allPods : pods).filter(p => p.active && p.type === 'polygon').map((pod) => {
                      const isSelected = selectedPod?.id === pod.id;
                      const path = getPolygonPath(pod);
                      if (path.length > 0) {
                        const centroid = getPolygonCentroid(path);
                        const podColor = getPodColor(pod.id);
                        const storeName = pod.store?.storeName || stores.find(s => s.id === pod.storeId)?.storeName || 'Unknown Store';
                        const travelDistance = pod.travelDistance || 'N/A';
                        
                        return (
                          <React.Fragment key={pod.id}>
                            <Polygon
                              paths={path}
                              onClick={(e) => handlePodClick(pod, e, false)}
                              options={{
                                fillColor: isSelected ? '#FFA500' : podColor,
                                fillOpacity: isSelected ? 0.4 : 0.2,
                                strokeColor: isSelected ? '#FF8C00' : podColor,
                                strokeOpacity: isSelected ? 1 : 0.8,
                                strokeWeight: isSelected ? 3 : 2,
                              }}
                            />
                            {centroid && selectedPod?.id === pod.id && (
                              <InfoWindow
                                position={centroid}
                                onCloseClick={() => setSelectedPod(null)}
                              >
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {storeName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Pod: {pod.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Distance: {typeof travelDistance === 'number' ? `${Math.round(travelDistance)}m` : travelDistance}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Status: {pod.active ? 'Active' : 'Inactive'}
                                  </Typography>
                                </Box>
                              </InfoWindow>
                            )}
                          </React.Fragment>
                        );
                      }
                      return null;
                    })}

                    {/* Drawing shapes should only appear in dialog, not on main map */}

                    {/* Distance measurement (main map) */}
                    {isMeasuringDistance && (
                      <>
                        {measurePoints.map((pt, idx) => (
                          <Marker
                            key={`measure-${idx}`}
                            position={pt}
                            label={idx === 0 ? 'A' : 'B'}
                            icon={window.google?.maps ? {
                              url: idx === 0
                                ? 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
                                : 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png',
                              scaledSize: new window.google.maps.Size(28, 28),
                            } : undefined}
                          />
                        ))}
                      </>
                    )}
                    
                    {/* Located marker */}
                    {locatedMarker && (
                      <Marker
                        position={locatedMarker}
                        title="Located Position"
                        icon={window.google?.maps ? {
                          url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                          scaledSize: new window.google.maps.Size(32, 32),
                        } : undefined}
                        label={window.google?.maps ? {
                          text: 'Located',
                          color: '#000',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        } : undefined}
                      />
                    )}

                    {/* Order markers */}
                    {ordersForVisualization.map((order) => {
                      const latLng = order.deliveryAddress?.latLng || order.deliveryAddress?.latLong;
                      if (!latLng || !latLng.lat || !latLng.lng) return null;
                      
                      const position = { lat: latLng.lat, lng: latLng.lng };
                      const isSelected = selectedOrderMarker?.orderId === (order.id || order.orderId);
                      
                      return (
                        <React.Fragment key={order.id || order.orderId}>
                          <Marker
                            position={position}
                            title={`Order #${order.orderId || order.id}`}
                            onClick={() => setSelectedOrderMarker({
                              orderId: order.orderId || order.id,
                              position,
                              order
                            })}
                            icon={window.google?.maps ? {
                              url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
                              scaledSize: new window.google.maps.Size(isSelected ? 32 : 24, isSelected ? 32 : 24),
                            } : undefined}
                          />
                          {isSelected && (
                            <InfoWindow
                              position={position}
                              onCloseClick={() => setSelectedOrderMarker(null)}
                            >
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  Order #{order.orderId || order.id}
                                </Typography>
                                {order.storeDetails?.storeName && (
                                  <Typography variant="body2" color="text.secondary">
                                    Store: {order.storeDetails.storeName}
                                  </Typography>
                                )}
                                {order.deliveryAddress?.userName && (
                                  <Typography variant="body2" color="text.secondary">
                                    Customer: {order.deliveryAddress.userName}
                                  </Typography>
                                )}
                                {order.totalAmount && (
                                  <Typography variant="body2" color="text.secondary">
                                    Amount: ₹{order.totalAmount}
                                  </Typography>
                                )}
                              </Box>
                            </InfoWindow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </GoogleMap>
                </Box>
                
                {/* Measure distance controls */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant={isMeasuringDistance ? 'contained' : 'outlined'}
                    startIcon={<Straighten />}
                    onClick={handleToggleMeasureDistance}
                  >
                    {isMeasuringDistance ? 'Measuring Distance' : 'Measure Distance'}
                  </Button>

                  {isMeasuringDistance && (
                    <>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={clearDistanceMeasurement}
                        disabled={measurePoints.length === 0}
                      >
                        Clear Points
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Click map for Point A, then Point B.
                      </Typography>
                      {measurePoints.length === 2 && (
                        <>
                          {isCalculatingTravelDistance ? (
                            <Chip
                              color="info"
                              label="Calculating travel distance..."
                            />
                          ) : (
                            <Chip
                              color="secondary"
                              label={`Travel Distance: ${(measuredDistanceMeters / 1000).toFixed(2)} km`}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>

                {/* Locate input field */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    label="Coordinates (lat, lng)"
                    placeholder="17.4416366, 78.3563825"
                    value={locateInput}
                    onChange={(e) => setLocateInput(e.target.value)}
                    fullWidth
                    size="small"
                    helperText="Enter coordinates in format: latitude, longitude"
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      const trimmed = locateInput.trim();
                      if (!trimmed) {
                        actions.showSnackbar('Please enter coordinates', 'error');
                        return;
                      }
                      
                      const parts = trimmed.split(',').map(coord => coord.trim());
                      if (parts.length !== 2) {
                        actions.showSnackbar('Invalid format. Please use: lat, lng', 'error');
                        return;
                      }
                      
                      const lat = parseFloat(parts[0]);
                      const lng = parseFloat(parts[1]);
                      
                      if (isNaN(lat) || isNaN(lng)) {
                        actions.showSnackbar('Invalid coordinates. Please enter valid numbers', 'error');
                        return;
                      }
                      
                      if (lat < -90 || lat > 90) {
                        actions.showSnackbar('Latitude must be between -90 and 90', 'error');
                        return;
                      }
                      
                      if (lng < -180 || lng > 180) {
                        actions.showSnackbar('Longitude must be between -180 and 180', 'error');
                        return;
                      }
                      
                      const position = { lat, lng };
                      setLocatedMarker(position);
                      
                      // Pan map to the location
                      if (map) {
                        map.panTo(position);
                        map.setZoom(15);
                      }
                      
                      actions.showSnackbar(`Marker added at (${lat}, ${lng})`, 'success');
                    }}
                  >
                    Locate
                  </Button>
                  {locatedMarker && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setLocatedMarker(null);
                        setLocateInput('');
                        actions.showSnackbar('Marker removed', 'info');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Pods List */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {viewAllPods ? 'All Pods (All Stores)' : `Pods for ${selectedStore?.storeName}`}
              </Typography>
              {podsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (viewAllPods ? allPods : pods).length === 0 ? (
                <Alert severity="info">
                  {viewAllPods ? 'No pods found across all stores.' : 'No pods found for this store. Create one to get started.'}
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {viewAllPods && <TableCell><strong>Store</strong></TableCell>}
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(viewAllPods ? allPods : pods).map((pod) => (
                        <TableRow key={pod.id} hover>
                          {viewAllPods && (
                            <TableCell>
                              {pod.store?.storeName || 'Unknown Store'}
                              
                            </TableCell>
                          )}
                          <TableCell>{pod.name}</TableCell>
                          <TableCell>
                            <Chip
                              label="Polygon"
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Switch
                                checked={pod.active || false}
                                onChange={(e) => handleTogglePodActive(pod, e.target.checked)}
                                size="small"
                                color="success"
                              />
                              <Typography variant="body2" sx={{ minWidth: 60 }}>
                                {pod.active ? 'Active' : 'Inactive'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteClick(pod)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Create Default Pods Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              {selectedStoreId && !viewAllPods && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="large"
                  onClick={handleCreateDefaultPodForStore}
                  disabled={creatingDefaultPodForStore || loading}
                >
                  {creatingDefaultPodForStore ? 'Creating...' : 'Create Default Pod for Store'}
                </Button>
              )}
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<Add />}
                onClick={handleCreateDefaultPods}
                disabled={creatingDefaultPods || loading}
              >
                {creatingDefaultPods ? 'Creating...' : 'Create Default Pods for All Stores'}
              </Button>
            </Box>
          </>
        )}

        {/* Create Pod Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => {
            resetDrawing();
            setCreateDialogOpen(false);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '95vh',
              height: '95vh',
            }
          }}
        >
          <DialogTitle>Create New Pod</DialogTitle>
          <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
              {/* Form Section - Top */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2, flexShrink: 0 }}>
                <TextField
                  label="Pod Name"
                  value={podName}
                  sx={{width:'400px'}}
                  onChange={(e) => setPodName(e.target.value)}
                  required
                  fullWidth
                />
                
                {/* Travel Distance Input - appears after marker is placed */}
                {showDistanceInput && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Travel Distance (meters)"
                      type="number"
                      sx={{width:'400px'}}
                      value={travelDistanceMeters}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setTravelDistanceMeters(value);
                        setPodGenerated(false);
                        setPreviewPolygon(null);
                      }}
                      inputProps={{ min: 100, step: 100 }}
                      size="medium"
                      required
                      helperText="Enter travel distance in meters (e.g., 5000 for 5km)"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{width:'400px'}}
                      onClick={handleGeneratePod}
                      disabled={travelDistanceMeters <= 0 || generatingPolygon}
                      startIcon={generatingPolygon ? <CircularProgress size={20} /> : null}
                    >
                      {generatingPolygon ? 'Generating...' : 'Generate Pod'}
                    </Button>
                    {podGenerated && (
                      <Alert severity="success">
                        Pod generated successfully! Click "Save" to create the pod.
                      </Alert>
                    )}
                  </Box>
                )}
                
                {!showDistanceInput && (
                  <Alert severity="info">
                    Click on the map to place a marker and set the pod center point
                  </Alert>
                )}
              </Box>

              {/* Map Section - Takes remaining space */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Map - Search for a place or click on the map to draw your pod
                </Typography>
                
                {/* Search Box */}
                {isLoaded && (
                  <Box sx={{ mb: 1, position: 'relative', zIndex: 1000 }}>
                    <StandaloneSearchBox
                      onLoad={onSearchBoxLoad}
                      onPlacesChanged={onPlacesChanged}
                    >
                      <TextField
                        fullWidth
                        placeholder="Search for a place..."
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#ddd',
                            },
                            '&:hover fieldset': {
                              borderColor: '#bbb',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </StandaloneSearchBox>
                  </Box>
                )}
                
                <Box 
                  sx={{ 
                    border: '1px solid #ddd', 
                    borderRadius: 1, 
                    overflow: 'hidden', 
                    flex: 1,
                    position: 'relative',
                    minHeight: '500px',
                    width: '100%'
                  }}
                >
                  {isLoaded ? (
                    <GoogleMap
                      key={`dialog-map-${createDialogOpen}-${selectedStoreId}`}
                      mapContainerStyle={{ width: '100%', height: '100%', minHeight: '500px' }}
                      center={mapCenter}
                      zoom={13}
                      onLoad={onDialogMapLoad}
                      onClick={handleDialogMapClick}
                      options={{
                        streetViewControl: false,
                        fullscreenControl: true,
                        mapTypeControl: true,
                        zoomControl: true,
                        clickableIcons: false,
                        draggable: true,
                      }}
                    >
                      {/* Store marker - always visible when store is selected */}
                      {selectedStore && selectedStore.location && (() => {
                        const [lat, lng] = selectedStore.location.split(',').map(coord => parseFloat(coord.trim()));
                        if (!isNaN(lat) && !isNaN(lng)) {
                          return (
                            <Marker
                              position={{ lat, lng }}
                              title={selectedStore.storeName}
                              icon={window.google?.maps ? {
                                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                                scaledSize: new window.google.maps.Size(32, 32),
                              } : undefined}
                              label={window.google?.maps ? {
                                text: selectedStore.storeName,
                                color: '#000',
                                fontSize: '14px',
                                fontWeight: 'bold',
                              } : undefined}
                            />
                          );
                        }
                        return null;
                      })()}

                      {/* Display existing pods */}
                      {(viewAllPods ? allPods : pods).filter(p => p.active && p.type === 'polygon').map((pod) => {
                        const isSelected = selectedDialogPod?.id === pod.id;
                        const path = getPolygonPath(pod);
                        if (path.length > 0) {
                          const centroid = getPolygonCentroid(path);
                          const podColor = getPodColor(pod.id);
                          const storeName = pod.store?.storeName || stores.find(s => s.id === pod.storeId)?.storeName || 'Unknown Store';
                          const travelDistance = pod.travelDistance || 'N/A';
                          
                          return (
                            <React.Fragment key={pod.id}>
                              <Polygon
                                paths={path}
                                onClick={(e) => handlePodClick(pod, e, true)}
                                options={{
                                  fillColor: isSelected ? '#FFA500' : podColor,
                                  fillOpacity: isSelected ? 0.4 : 0.2,
                                  strokeColor: isSelected ? '#FF8C00' : podColor,
                                  strokeOpacity: isSelected ? 1 : 0.8,
                                  strokeWeight: isSelected ? 3 : 2,
                                }}
                              />
                              {centroid && selectedDialogPod?.id === pod.id && (
                                <InfoWindow
                                  position={centroid}
                                  onCloseClick={() => setSelectedDialogPod(null)}
                                >
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {storeName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Pod: {pod.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Distance: {typeof travelDistance === 'number' ? `${Math.round(travelDistance)}m` : travelDistance}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Status: {pod.active ? 'Active' : 'Inactive'}
                                    </Typography>
                                  </Box>
                                </InfoWindow>
                              )}
                            </React.Fragment>
                          );
                        }
                        return null;
                      })}

                      {/* Selected pod center marker */}
                      {selectedPodCenter && (
                        <Marker
                          position={selectedPodCenter}
                          title="Pod Center"
                          icon={window.google?.maps ? {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: '#00FF00',
                            fillOpacity: 1,
                            strokeColor: '#FFFFFF',
                            strokeWeight: 2,
                          } : undefined}
                        />
                      )}

                      {/* Preview polygon after generation */}
                      {previewPolygon && previewPolygon.length > 0 && (
                        <Polygon
                          paths={previewPolygon}
                          options={{
                            fillColor: '#00FF00',
                            fillOpacity: 0.2,
                            strokeColor: '#00FF00',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      )}
                    </GoogleMap>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary">
                        Loading map...
                      </Typography>
                    </Box>
                  )}
                  {!selectedPodCenter && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        p: 1.5,
                        borderRadius: 1,
                        zIndex: 1000,
                        boxShadow: 2,
                        border: '2px solid',
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        🎯 Click on the map to place pod center point
                      </Typography>
                    </Box>
                  )}
                  {selectedPodCenter && !podGenerated && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        p: 1.5,
                        borderRadius: 1,
                        zIndex: 1000,
                        boxShadow: 2,
                        border: '2px solid',
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        🎯 Enter travel distance and click "Generate Pod"
                      </Typography>
                    </Box>
                  )}
                  {podGenerated && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        bgcolor: 'rgba(76, 175, 80, 0.95)',
                        p: 1.5,
                        borderRadius: 1,
                        zIndex: 1000,
                        boxShadow: 2,
                        border: '2px solid',
                        borderColor: 'success.main',
                      }}
                    >
                      <Typography variant="body2" color="white" fontWeight="bold">
                        ✅ Pod generated! Click "Save" to create the pod.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              resetDrawing();
              setCreateDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreatePod}
              variant="contained"
              disabled={!podName.trim() || !podGenerated}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Visualize Orders Dialog */}
        <Dialog open={visualizeOrdersDialogOpen} onClose={handleCloseVisualizeOrdersDialog}>
          <DialogTitle>Visualize Orders on Map</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, minWidth: 400 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Select a date range (maximum 31 days) to visualize orders on the map along with delivery pods.
              </Alert>
              
              <TextField
                label="Start Date"
                type="date"
                value={orderFromDate}
                onChange={(e) => setOrderFromDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0] // Prevent future dates
                }}
              />
              
              <TextField
                label="End Date"
                type="date"
                value={orderToDate}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setOrderToDate(selectedDate);
                  
                  // Validate 31-day limit
                  if (orderFromDate && selectedDate) {
                    const fromDate = new Date(orderFromDate);
                    const toDate = new Date(selectedDate);
                    const daysDiff = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24));
                    if (daysDiff > 31) {
                      actions.showSnackbar('Date range cannot exceed 31 days', 'error');
                      setOrderToDate('');
                    }
                  }
                }}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: orderFromDate || undefined,
                  max: new Date().toISOString().split('T')[0] // Prevent future dates
                }}
              />
              
              {orderFromDate && orderToDate && (
                <Typography variant="body2" color="text.secondary">
                  Selected range: {Math.ceil((new Date(orderToDate) - new Date(orderFromDate)) / (1000 * 60 * 60 * 24))} day(s)
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVisualizeOrdersDialog}>Cancel</Button>
            <Button
              onClick={handleFetchOrdersForVisualization}
              variant="contained"
              disabled={!orderFromDate || !orderToDate || loadingOrders}
            >
              {loadingOrders ? 'Loading...' : 'Show'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Pod</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the pod "{podToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default PodsManagement;
